/**
 * Fjalori i aseteve — një burim i vetëm për kategoritë, gjendjet dhe statuset.
 *
 * Pasqyron `backend/services/assets.js`. Kur shtohet një kategori ose një
 * status, shtohet në të dy anët: serveri e refuzon një vlerë jashtë listës dhe
 * e zëvendëson me parazgjedhjen, ndaj një etiketë e re vetëm këtu do të ruhej
 * si diçka tjetër nga ç'zgjodhi përdoruesi. `assets.test.js` i mban të njëjta.
 *
 * Ngjyrat nuk jetojnë këtu: statuset dhe gjendjet kalojnë nëpër `StatusChip`
 * (fjalori i përbashkët i aplikacionit), ndaj një aset "Në servis" duket
 * saktësisht si çdo gjendje tjetër "prite" në sistem. Kategoritë dallohen me
 * IKONË, jo me ngjyrë — nëntë hue-e do ta kthenin regjistrin në ylber dhe
 * ngjyra do të pushonte së qeni informacion.
 */

import LaptopMacIcon        from '@mui/icons-material/LaptopMac';
import DesktopWindowsIcon   from '@mui/icons-material/DesktopWindows';
import SmartphoneIcon       from '@mui/icons-material/Smartphone';
import MonitorIcon          from '@mui/icons-material/Monitor';
import DirectionsCarIcon    from '@mui/icons-material/DirectionsCar';
import HandymanIcon         from '@mui/icons-material/Handyman';
import ChairIcon            from '@mui/icons-material/Chair';
import VpnKeyIcon           from '@mui/icons-material/VpnKey';
import Inventory2Icon       from '@mui/icons-material/Inventory2';

// ── Kategoritë ──────────────────────────────────────────────────────────────
export const CATEGORIES = [
  { key: 'laptop',    label: 'Laptop',          Icon: LaptopMacIcon },
  { key: 'desktop',   label: 'Kompjuter',       Icon: DesktopWindowsIcon },
  { key: 'phone',     label: 'Telefon',         Icon: SmartphoneIcon },
  { key: 'monitor',   label: 'Monitor',         Icon: MonitorIcon },
  { key: 'vehicle',   label: 'Automjet',        Icon: DirectionsCarIcon },
  { key: 'tool',      label: 'Pajisje pune',    Icon: HandymanIcon },
  { key: 'furniture', label: 'Mobilje',         Icon: ChairIcon },
  { key: 'access',    label: 'Qasje & çelësa',  Icon: VpnKeyIcon },
  { key: 'other',     label: 'Tjetër',          Icon: Inventory2Icon },
];

// ── Gjendja fizike ──────────────────────────────────────────────────────────
// E ndarë me qëllim nga statusi: një pajisje E DËMTUAR mund të jetë ende te
// dikush, dhe një pajisje E RE mund të rrijë në depo.
export const CONDITIONS = [
  { key: 'new',      label: 'E re',              chip: 'cond_new' },
  { key: 'good',     label: 'E mirë',            chip: 'cond_good' },
  { key: 'damaged',  label: 'E dëmtuar',         chip: 'cond_damaged' },
  { key: 'unusable', label: 'Jashtë përdorimit', chip: 'cond_unusable' },
];

// ── Statusi ─────────────────────────────────────────────────────────────────
export const STATUSES = [
  { key: 'available', label: 'Në dispozicion', chip: 'asset_available' },
  { key: 'assigned',  label: 'E caktuar',      chip: 'asset_assigned' },
  { key: 'service',   label: 'Në servis',      chip: 'asset_service' },
  { key: 'lost',      label: 'E humbur',       chip: 'asset_lost' },
  { key: 'retired',   label: 'E larguar',      chip: 'asset_retired' },
];

export const CATEGORY_KEYS  = CATEGORIES.map((c) => c.key);
export const CONDITION_KEYS = CONDITIONS.map((c) => c.key);
export const STATUS_KEYS    = STATUSES.map((s) => s.key);

const find = (list, key) => list.find((x) => x.key === key) || null;

export const categoryMeta  = (k) => find(CATEGORIES, k) || find(CATEGORIES, 'other');
export const conditionMeta = (k) => find(CONDITIONS, k) || find(CONDITIONS, 'good');
export const statusMeta    = (k) => find(STATUSES, k)   || find(STATUSES, 'available');

export const categoryLabel  = (k) => categoryMeta(k).label;
export const conditionLabel = (k) => conditionMeta(k).label;
export const statusLabel    = (k) => statusMeta(k).label;

/** Çelësi i `StatusChip` për një status ose gjendje. */
export const statusChip    = (k) => statusMeta(k).chip;
export const conditionChip = (k) => conditionMeta(k).chip;

// ── Dosja: identifikuesi, specifikimet, financat ────────────────────────────

/**
 * Si quhet numri identifikues i secilës kategori.
 *
 * "Serial Number" mbi një automjet është i gabuar dhe mbi një telefon i paplotë —
 * njeriu që e mban regjistrin shkruan VIN dhe IMEI. Kolona mbetet një
 * (`serialNumber`); ndryshon vetëm etiketa, ndaj kërkimi i gjen të gjitha me një
 * fushë. Pasqyron `IDENTIFIER_LABEL` te backend-i.
 */
const IDENTIFIER_LABEL = { phone: 'IMEI', vehicle: 'VIN / Shasia' };
export const identifierLabel = (category) => IDENTIFIER_LABEL[category] || 'Serial Number';

/**
 * Specifikimet teknike — çelësa sipas kategorisë, jo kolona fikse.
 * Një `cpu` bosh te çdo karrige do të ishte fushë që s'e mbush askush.
 */
export const SPEC_FIELDS = {
  laptop:    [['cpu', 'CPU'], ['ram', 'RAM'], ['storage', 'Storage / SSD'], ['os', 'Sistemi Operativ'], ['color', 'Ngjyra'], ['ip', 'IP Adresa']],
  desktop:   [['cpu', 'CPU'], ['ram', 'RAM'], ['storage', 'Storage / SSD'], ['os', 'Sistemi Operativ'], ['ip', 'IP Adresa']],
  phone:     [['os', 'Sistemi Operativ'], ['storage', 'Memoria'], ['color', 'Ngjyra'], ['number', 'Numri i telefonit']],
  monitor:   [['size', 'Diagonalja'], ['resolution', 'Rezolucioni'], ['ports', 'Portat']],
  vehicle:   [['plate', 'Targat'], ['year', 'Viti'], ['fuel', 'Karburanti'], ['mileage', 'Kilometrazhi'], ['insurance_until', 'Sigurimi deri më']],
  tool:      [['power', 'Fuqia'], ['color', 'Ngjyra']],
  furniture: [['material', 'Materiali'], ['color', 'Ngjyra'], ['size', 'Përmasat']],
  access:    [['zone', 'Zona / Dera'], ['code', 'Kodi']],
  other:     [['note', 'Hollësi']],
};
export const specFields = (category) => SPEC_FIELDS[category] || SPEC_FIELDS.other;

/**
 * Identitet i qëndrueshëm për një rresht të redaktueshëm.
 *
 * Ekziston sepse çelësi i React-it NUK guxon të dalë nga përmbajtja që
 * redaktohet. Varianti i parë e ndërtonte nga `key`-i i specifikimit; sapo
 * shkruaje shkronjën e parë te emri, çelësi ndryshonte, React e çmontonte
 * rreshtin dhe **fusha humbte fokusin pas çdo shkronje** — dukej sikur asgjë
 * nuk ruhej. Indeksi si çelës e ka të njëjtin defekt te fshirja: rreshtat pas
 * atij të fshirë rinumërohen dhe React i ripërdor kutitë në vend të gabuar.
 *
 * Numërues, jo `Math.random()`: i njëjti rezultat në çdo ekzekutim, ndaj testet
 * mbeten të parashikueshme.
 */
let _uid = 0;
export const rowUid = () => `r${(_uid += 1)}`;

/**
 * Specifikimet janë listë e HAPUR, jo formular i mbyllur.
 *
 * `SPEC_FIELDS` mbetet, por si **sugjerim**: një laptop nis me CPU/RAM/SSD sepse
 * ato pyeten gjithmonë. Ajo që u shtua është mundësia për të shkruar çka të
 * duash — «Numri i shasisë», «Licenca», «Kapaciteti i baterisë» — pa pritur që
 * dikush ta shtojë te kodi.
 *
 * Çelësi ruhet i njohur kur fusha është e njohur (`cpu`, `ram`, `plate`), dhe
 * VETË ETIKETA kur nuk është. Kështu një specifikim i shpikur nga përdoruesi
 * lexohet saktësisht si e shkroi ai — pa u kthyer në `numri_i_shasise` — kurse
 * fushat e njohura mbeten të krahasueshme mes aseteve.
 */
const foldLabel = (s) => String(s ?? '').trim().normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toLowerCase();

const SPEC_PAIRS = Object.values(SPEC_FIELDS).flat();

/**
 * Çelës → etiketë. I njëjti çelës mban emra të ndryshëm sipas kategorisë
 * (`storage` është «Storage / SSD» te laptopi dhe «Memoria» te telefoni); për
 * SHFAQJEN mbetet i pari, sepse një çelës i huaj duhet të ketë një emër, jo
 * dy.
 */
const SPEC_LABELS = {};
SPEC_PAIRS.forEach(([k, label]) => { if (!(k in SPEC_LABELS)) SPEC_LABELS[k] = label; });

/**
 * Etiketë → çelës, me TË GJITHA etiketat brenda.
 *
 * Varianti i parë e kërkonte çelësin duke shfletuar `SPEC_LABELS`, pra vetëm
 * një emër për çelës — dhe emrat e tjerë të të njëjtit çelës humbnin: «Storage
 * / SSD» nuk njihej më si `storage`, sepse te harta kishte mbetur «Memoria».
 * Pasoja dukej te importi i aseteve: specifikimi ruhej si çelës i shpikur dhe
 * te formulari dilte dy herë — një rresht bosh «Storage / SSD» dhe një i
 * mbushur me të njëjtin emër.
 */
const KEY_BY_LABEL = new Map(SPEC_PAIRS.map(([k, label]) => [foldLabel(label), k]));

/** Të gjitha etiketat e njohura — ushqejnë sugjerimet e fushës së emrit. */
export const SPEC_SUGGESTIONS = Array.from(new Set(SPEC_PAIRS.map(([, label]) => label))).sort(
  (a, b) => a.localeCompare(b, 'sq'),
);

/** Etiketë → çelës: i njohuri merr çelësin kanonik, i shpikuri mban vetveten. */
export function specKey(label) {
  const wanted = foldLabel(label);
  if (!wanted) return '';
  return KEY_BY_LABEL.get(wanted) || String(label).trim().slice(0, 40);
}

/** Çelës → etiketë. Çelësi i panjohur ËSHTË etiketa. */
export const specLabel = (key) => SPEC_LABELS[key] || String(key || '');

/**
 * Rreshtat e specifikimeve për formularin: sugjerimet e kategorisë (edhe kur
 * janë bosh, që të mos duhet t'i shkruash) plus çdo çelës që aseti mban tashmë.
 * Rendi është ai i sugjerimeve, pastaj shtesat sipas rendit të ruajtjes.
 */
export function specRows(specs = {}, category) {
  const rows = specFields(category).map(([key, label]) => ({
    uid: rowUid(), key, label, value: specs?.[key] ?? '',
  }));
  const seen = new Set(rows.map((r) => r.key));
  for (const [key, value] of Object.entries(specs || {})) {
    if (!seen.has(key)) rows.push({ uid: rowUid(), key, label: specLabel(key), value: value ?? '' });
  }
  return rows;
}

/** Rreshtat → objekti që ruhet. Rreshtat pa emër ose pa vlerë nuk hyjnë. */
export function specsFromRows(rows = []) {
  const out = {};
  for (const r of rows) {
    const key = specKey(r?.label);
    const value = String(r?.value ?? '').trim();
    if (key && value) out[key] = value;
  }
  return out;
}

/** Vetëm ato që kanë vlerë — për faqen e detajeve. */
export function filledSpecs(specs = {}) {
  return Object.entries(specs || {})
    .filter(([, v]) => String(v ?? '').trim() !== '')
    .map(([key, value]) => ({ key, label: specLabel(key), value }));
}

export const PAYMENT_METHODS = [
  { key: 'bank',    label: 'Transfer bankar' },
  { key: 'cash',    label: 'Para në dorë' },
  { key: 'card',    label: 'Kartë' },
  { key: 'leasing', label: 'Leasing' },
  { key: 'other',   label: 'Tjetër' },
];

export const SERVICE_STATUSES = [
  { key: 'planned',     label: 'I planifikuar', chip: 'upcoming' },
  { key: 'in_progress', label: 'Në proces',     chip: 'active' },
  { key: 'completed',   label: 'Përfunduar',    chip: 'completed' },
  { key: 'cancelled',   label: 'Anuluar',       chip: 'reversed' },
];

export const INCIDENT_KINDS = [
  { key: 'damage',      label: 'Dëmtim' },
  { key: 'loss',        label: 'Humbje' },
  { key: 'theft',       label: 'Vjedhje' },
  { key: 'malfunction', label: 'Defekt teknik' },
  { key: 'other',       label: 'Tjetër' },
];

export const paymentLabel  = (k) => (PAYMENT_METHODS.find((x) => x.key === k) || {}).label || '';
export const serviceMeta   = (k) => SERVICE_STATUSES.find((x) => x.key === k) || SERVICE_STATUSES[2];
export const serviceLabel  = (k) => serviceMeta(k).label;
export const serviceChip   = (k) => serviceMeta(k).chip;
export const incidentLabel = (k) => (INCIDENT_KINDS.find((x) => x.key === k) || {}).label || 'Incident';

/**
 * Vlera e mbetur pas amortizimit linear — pasqyrë e `residualValue` te serveri.
 * Serveri e kthen te `asset.residualValue`; kjo shërben për parapamje te
 * formulari, ku aseti ende s'është ruajtur.
 */
export function residualValue({ purchaseValue, purchaseDate, amortizationYears }, today) {
  const value = Number(purchaseValue);
  const years = Number(amortizationYears);
  if (!Number.isFinite(value) || !(years > 0)) return null;
  const ok = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || '').slice(0, 10));
  if (!ok(purchaseDate) || !ok(today)) return null;
  const days = (Date.parse(today) - Date.parse(purchaseDate)) / 86400000;
  if (!Number.isFinite(days) || days < 0) return Math.round(value * 100) / 100;
  const used = Math.min(1, days / (years * 365.25));
  return Math.round(value * (1 - used) * 100) / 100;
}

// ── Rregullat që UI-ja duhet t'i dijë PARA klikimit ─────────────────────────

/**
 * Statuset nga të cilat një pajisje nuk mund të dorëzohet. Pasqyron
 * `NOT_ASSIGNABLE` te shërbimi: serveri e refuzon gjithsesi, por butoni nuk
 * guxon të ftojë në një veprim që dihet se do të refuzohet.
 */
const NOT_ASSIGNABLE = new Set(['assigned', 'lost', 'retired', 'service']);

export const canAssign = (asset) => !!asset && !NOT_ASSIGNABLE.has(asset.status);
export const canReturn = (asset) => !!asset && asset.status === 'assigned';

/**
 * Statusi që do të marrë pajisja pas kthimit — pasqyrë e `statusAfterReturn`
 * te backend-i. Këtu shërben vetëm për ta THËNË paraprakisht te dialogu
 * ("Pas kthimit: Në servis"); e vërteta vjen nga përgjigjja e serverit.
 */
export function previewStatusAfterReturn(condition, requestedStatus) {
  if (requestedStatus === 'lost' || requestedStatus === 'retired') return requestedStatus;
  if (condition === 'unusable') return 'retired';
  if (condition === 'damaged')  return 'service';
  if (requestedStatus === 'service') return 'service';
  return 'available';
}

/** Kthim me dëm → dialogu hap fushat e kostos dhe të ndalesës. */
export const isDamagedReturn = (condition) => condition === 'damaged' || condition === 'unusable';

/**
 * Deklarata e përgjegjësisë — teksti që nënshkruan punonjësi.
 *
 * Rri KËTU, jo brenda faqes: i njëjti tekst shkon te ekrani, te dokumenti i
 * printuar dhe te PDF-ja. Tri kopje do të thonin tri formulime, dhe pikërisht
 * ky paragraf është ai që lexohet kur dikush e mohon dorëzimin.
 */
export const DEKLARATA = {
  handover: 'Unë, punonjësi pranues, deklaroj se i kam pranuar asetet e lartcekura në gjendjen e '
    + 'specifikuar dhe jam përgjegjës/e për ruajtjen, përdorimin e duhur dhe kthimin e tyre në '
    + 'momentin e kërkuar nga kompania. Në rast dëmtimi, humbjeje ose moskthimi, pajtohem që të mbaj '
    + 'përgjegjësi materiale sipas rregullores së kompanisë dhe legjislacionit në fuqi.',
  ret: 'Unë, përfaqësuesi i kompanisë, konfirmoj se i kam pranuar asetet e lartcekura nga punonjësi '
    + 'në gjendjen e specifikuar. Dëmtimet e konstatuara janë shënuar në këtë procesverbal dhe '
    + 'trajtohen sipas rregullores së kompanisë.',
};

// ── Formatim ────────────────────────────────────────────────────────────────

/** Data si e shkruan njeriu (dd.mm.yyyy), ose "—" kur mungon. */
export function dateText(v) {
  const s = String(v || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
  const [y, m, d] = s.split('-');
  return `${d}.${m}.${y}`;
}

/** Shuma me monedhë. `null` lexohet si mungesë, jo si zero. */
export function money(v, currency = 'EUR') {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

/** Emri i plotë i pajisjes për listat: markë + model, ose vetëm emri. */
export function assetTitle(a) {
  if (!a) return '—';
  const detail = [a.brand, a.model].filter(Boolean).join(' ');
  return detail && detail !== a.name ? `${a.name} · ${detail}` : (a.name || '—');
}

/**
 * A ka skaduar garancioni më `today`?
 *
 * `today` hyn si parametër, jo `new Date()` brenda: përndryshe kolona
 * "Garancioni" do të ishte e patestueshme pa ngrirë orën e sistemit.
 */
export function warrantyState(warrantyUntil, today) {
  const until = String(warrantyUntil || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(until)) return 'none';
  const day = String(today || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return 'none';
  return until < day ? 'expired' : 'valid';
}

// ── Kërkimi dhe filtrat ─────────────────────────────────────────────────────

/** Pa thekse dhe pa shkronja të mëdha — "verejtje" gjen "vërejtje". */
const fold = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * Rregullat e kërkimit rrinë këtu, jo brenda një `useMemo` që s'e prek dot
 * asnjë test. Kërkohet te çdo fushë me të cilën një njeri do ta kërkonte një
 * pajisje: kodi, emri, seria, marka, modeli dhe kush e mban.
 */
export function matchesQuery(asset, q) {
  const term = fold(q).trim();
  if (!term) return true;
  const hay = [
    asset.assetCode, asset.name, asset.brand, asset.model,
    asset.serialNumber, asset.location, asset.supplier,
    asset.holder?.name,
  ].map(fold).join(' ');
  return term.split(/\s+/).every((w) => hay.includes(w));
}

/** Shifrat e regjistrit — pasqyrë e `summarize` te backend-i. */
export function summarize(assets = []) {
  const by = (k) => assets.filter((a) => a.status === k).length;
  return {
    total:     assets.length,
    available: by('available'),
    assigned:  by('assigned'),
    service:   by('service'),
    lost:      by('lost'),
    retired:   by('retired'),
    value:     Math.round(assets.reduce((s, a) => s + (Number(a.purchaseValue) || 0), 0) * 100) / 100,
  };
}
